if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.track = this.querySelector('.product-media-modal__content');
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this.index = 0;
        this.zoomed = null;
        if (this.track && this.dialog) {
          this.buildControls();
          this.bindEvents();
        }
      }

      get slides() {
        return Array.from(this.track.children).filter((el) => el.hasAttribute('data-media-id'));
      }

      buildControls() {
        const icon = (d) =>
          `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        const makeButton = (cls, label, path) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `zv-lb-btn ${cls}`;
          button.setAttribute('aria-label', label);
          button.innerHTML = icon(path);
          this.dialog.appendChild(button);
          return button;
        };
        this.prevButton = makeButton('zv-lb-prev', 'Imagen anterior', 'M15 5l-7 7 7 7');
        this.nextButton = makeButton('zv-lb-next', 'Imagen siguiente', 'M9 5l7 7-7 7');

        this.counter = document.createElement('div');
        this.counter.className = 'zv-lb-counter';
        this.counter.setAttribute('aria-live', 'polite');
        this.dots = document.createElement('div');
        this.dots.className = 'zv-lb-dots';
        this.dots.setAttribute('aria-hidden', 'true');
        this.counterText = document.createElement('span');
        this.counterText.className = 'visually-hidden';
        this.counter.append(this.dots, this.counterText);
        this.dialog.appendChild(this.counter);
      }

      bindEvents() {
        const stop = (event) => event.stopPropagation();
        [this.prevButton, this.nextButton, this.counter].forEach((el) => el.addEventListener('pointerup', stop));
        this.prevButton.addEventListener('click', () => this.go(this.index - 1));
        this.nextButton.addEventListener('click', () => this.go(this.index + 1));

        this.track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(this.scrollFrame);
            this.scrollFrame = requestAnimationFrame(() => this.updateUI());
          },
          { passive: true }
        );

        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.go(this.index + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.go(this.index - 1);
          }
        });

        this.track.addEventListener('pointerdown', (event) => {
          this.pointerStart = { x: event.clientX, y: event.clientY };
        });
        this.track.addEventListener('pointermove', (event) => {
          if (this.zoomed && event.target === this.zoomed) this.setOrigin(this.zoomed, event);
        });
        this.track.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
      }

      onPointerUp(event) {
        const img = event.target.closest('img');
        const start = this.pointerStart;
        const moved = start ? Math.hypot(event.clientX - start.x, event.clientY - start.y) : 0;
        this.pointerStart = null;

        if (this.zoomed) {
          event.stopPropagation();
          if (moved < 8 || event.pointerType === 'mouse') this.unzoom();
          return;
        }
        if (!img || img.parentElement !== this.track) return;
        if (moved > 8) {
          event.stopPropagation();
          return;
        }
        if (!this.isInsideImage(img, event)) return; // clic fuera de la foto: cierra como siempre
        event.stopPropagation();
        this.zoom(img, event);
      }

      contentRect(img) {
        const rect = img.getBoundingClientRect();
        const style = getComputedStyle(img);
        const pl = parseFloat(style.paddingLeft) || 0;
        const pr = parseFloat(style.paddingRight) || 0;
        const pt = parseFloat(style.paddingTop) || 0;
        const pb = parseFloat(style.paddingBottom) || 0;
        const boxW = rect.width - pl - pr;
        const boxH = rect.height - pt - pb;
        const ratio = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : 1;
        let w = boxW;
        let h = boxW / ratio;
        if (h > boxH) {
          h = boxH;
          w = boxH * ratio;
        }
        return { left: rect.left + pl + (boxW - w) / 2, top: rect.top + pt + (boxH - h) / 2, width: w, height: h };
      }

      isInsideImage(img, event) {
        const r = this.contentRect(img);
        return event.clientX >= r.left && event.clientX <= r.left + r.width && event.clientY >= r.top && event.clientY <= r.top + r.height;
      }

      setOrigin(img, event) {
        const r = this.baseRect || img.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((event.clientX - r.left) / r.width) * 100));
        const y = Math.min(100, Math.max(0, ((event.clientY - r.top) / r.height) * 100));
        img.style.transformOrigin = `${x}% ${y}%`;
      }

      zoom(img, event) {
        this.baseRect = img.getBoundingClientRect();
        this.setOrigin(img, event);
        img.classList.add('zv-zoomed');
        this.zoomed = img;
        this.track.style.overflowX = 'hidden';
      }

      unzoom() {
        if (!this.zoomed) return;
        this.zoomed.classList.remove('zv-zoomed');
        const img = this.zoomed;
        setTimeout(() => {
          if (!img.classList.contains('zv-zoomed')) img.style.transformOrigin = '';
        }, 320);
        this.zoomed = null;
        this.baseRect = null;
        this.track.style.overflowX = '';
      }

      go(index) {
        const slides = this.slides;
        if (!slides.length) return;
        const target = Math.max(0, Math.min(slides.length - 1, index));
        this.unzoom();
        this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
      }

      updateUI() {
        const slides = this.slides;
        const width = this.track.clientWidth || 1;
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(this.track.scrollLeft / width)));
        if (index !== this.index && this.zoomed) this.unzoom();
        this.index = index;
        slides.forEach((slide, i) => slide.classList.toggle('active', i === index));

        if (this.dots.childElementCount !== slides.length) {
          this.dots.innerHTML = slides.map(() => '<span></span>').join('');
        }
        Array.from(this.dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        this.counterText.textContent = `Imagen ${index + 1} de ${slides.length}`;
        this.prevButton.disabled = index === 0;
        this.nextButton.disabled = index === slides.length - 1;
        this.counter.hidden = slides.length < 2;
      }

      hide() {
        this.unzoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const slides = this.slides;
        const mediaId = this.openedBy && this.openedBy.getAttribute('data-media-id');
        let index = slides.findIndex((slide) => slide.getAttribute('data-media-id') === mediaId);
        if (index < 0) index = 0;
        const activeMedia = slides[index];
        if (!activeMedia) return;

        this.unzoom();
        const place = () => {
          this.track.scrollLeft = index * this.track.clientWidth;
          this.updateUI();
        };
        place();
        requestAnimationFrame(place);

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
